import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  RefreshCcw,
  Activity,
  Database,
  HardDrive,
  Zap,
  Webhook,
  ShieldCheck,
  PlayCircle,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Rocket,
  Mail,
  Send,
  ScrollText,
  Megaphone,
  Download,
  Upload,
  Bot,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Production cold-start / transient-network hardening. The Ops page is the
// first thing super admins hit after a long idle period, so the underlying
// pod is frequently waking up — the first request can flake (network error
// or 502/503/504) while subsequent ones succeed. Wrap each call so the user
// doesn't have to "click twice" anymore.
const _isTransientError = (e) => {
  if (!e) return false;
  if (e.code === 'ECONNABORTED' || e.code === 'ERR_NETWORK') return true;
  const s = e?.response?.status;
  return s === 502 || s === 503 || s === 504;
};
const withRetry = async (fn, attempts = 2, delayMs = 800) => {
  let lastErr;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (i === attempts - 1 || !_isTransientError(e)) throw e;
      await new Promise((r) => setTimeout(r, delayMs * (i + 1)));
    }
  }
  throw lastErr;
};

function fmtBytes(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function StatusIcon({ tone }) {
  if (tone === 'ok') return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
  if (tone === 'warn') return <AlertTriangle className="w-5 h-5 text-amber-600" />;
  return <XCircle className="w-5 h-5 text-red-600" />;
}

function ToneBadge({ tone, children }) {
  const map = {
    ok: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    warn: 'bg-amber-100 text-amber-800 border-amber-200',
    bad: 'bg-red-100 text-red-800 border-red-200',
    neutral: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return (
    <Badge variant="outline" className={map[tone] || map.neutral}>
      {children}
    </Badge>
  );
}

export default function OpsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [referralBoard, setReferralBoard] = useState({ leaderboard: [], total_referred_signups: 0 });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const res = await withRetry(() => axios.get(`${API}/super-admin/ops`, { headers, timeout: 20000 }));
      setSnap(res.data);
    } catch (e) {
      toast.error('Failed to load ops snapshot');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(id);
  }, []);

  // Referral leaderboard — loaded on mount + on every ops refresh so admins
  // can spot the most enthusiastic ambassadors. Silent-fails so a temporary
  // 500 on this endpoint can never break the Ops page.
  useEffect(() => {
    const loadBoard = async () => {
      try {
        const { data } = await axios.get(`${API}/super-admin/referral-leaderboard`, { headers, timeout: 15000 });
        setReferralBoard(data || { leaderboard: [], total_referred_signups: 0 });
      } catch { /* silent — the card just stays empty */ }
    };
    loadBoard();
    const id = setInterval(loadBoard, 60000);
    return () => clearInterval(id);
  }, []);

  const runBackup = async () => {
    setBackingUp(true);
    try {
      await withRetry(() => axios.post(`${API}/super-admin/ops/backup`, {}, { headers, timeout: 130000 }));
      toast.success('Backup completed');
      await load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backup failed');
    } finally {
      setBackingUp(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading ops snapshot…
      </div>
    );
  }

  if (!snap) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Unable to load ops data.
      </div>
    );
  }

  const health = snap.health || {};
  const backups = snap.backups || {};
  const llm = snap.llm || {};
  const webhook = snap.stripe_webhook || {};
  const limits = snap.rate_limits || {};
  const audit = snap.audit || {};

  // Tones
  const dbTone = health.db === 'ok' ? 'ok' : 'bad';
  const backupTone =
    !backups.last_backup
      ? 'bad'
      : backups.hours_since_backup > 36
      ? 'warn'
      : 'ok';
  const llmTone = llm.state === 'exceeded' ? 'bad' : llm.state === 'warning' ? 'warn' : 'ok';
  const webhookTone = webhook.secret_configured ? 'ok' : 'warn';
  // Flag a spike: >50 admin actions in the last 24h is unusual and worth a glance
  const auditTone = audit.last_24h > 50 ? 'warn' : 'ok';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ops-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/super-admin')}
              data-testid="ops-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-600" />
                Platform Ops
              </h1>
              <p className="text-xs text-gray-500">
                Snapshot generated {fmtAgo(snap.generated_at)} · auto-refresh 30s
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="ops-refresh-btn"
          >
            <RefreshCcw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Pre-launch readiness gate */}
        <ReadinessCard readiness={snap.readiness} />

        {/* Soft-launch banner control */}
        <BetaModeCard token={token} />

        {/* CMS Export / Import — sync content between preview and production */}
        <CmsTransferCard token={token} />

        {/* Top status grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <StatusTile
            tone={dbTone}
            icon={<Database className="w-5 h-5" />}
            label="API & Database"
            value={health.status === 'healthy' ? 'Healthy' : 'Degraded'}
            sub={
              health.db_latency_ms != null
                ? `DB ping ${health.db_latency_ms} ms`
                : health.db_error || '—'
            }
          />
          <StatusTile
            tone={backupTone}
            icon={<HardDrive className="w-5 h-5" />}
            label="Mongo Backup"
            value={
              backups.last_backup
                ? fmtAgo(backups.last_backup.modified_at)
                : 'No backups'
            }
            sub={
              backups.last_backup
                ? `${fmtBytes(backups.last_backup.size_bytes)} · ${backups.archive_count} total`
                : `dir: ${backups.directory}`
            }
          />
          <StatusTile
            tone={llmTone}
            icon={<Zap className="w-5 h-5" />}
            label="LLM Budget"
            value={`${(llm.calls_this_month || 0).toLocaleString()} / ${(llm.threshold || 0).toLocaleString()}`}
            sub={`${llm.pct_used || 0}% used this month`}
          />
          <StatusTile
            tone={webhookTone}
            icon={<Webhook className="w-5 h-5" />}
            label="Stripe Webhook"
            value={webhook.secret_configured ? 'Verified' : 'Dev fallback'}
            sub={
              webhook.secret_configured
                ? 'Signature verification active'
                : 'STRIPE_WEBHOOK_SECRET not set'
            }
          />
          <StatusTile
            tone={auditTone}
            icon={<ScrollText className="w-5 h-5" />}
            label="Admin Activity"
            value={`${(audit.last_24h || 0).toLocaleString()} in 24h`}
            sub={
              audit.last_action_at
                ? `${(audit.total || 0).toLocaleString()} total · last ${fmtAgo(audit.last_action_at)}`
                : `${(audit.total || 0).toLocaleString()} total · no recent activity`
            }
            onClick={() => navigate('/super-admin/audit-log')}
          />
        </div>

        {/* Referral Leaderboard — top coaches by public-share signups.
            Renders inline (not a separate card component) so it stays inside
            the main OpsPage scope with direct access to `referralBoard`. */}
        <Card data-testid="ops-referral-leaderboard-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy className="w-4 h-4 text-emerald-600" />
              Top Referring Coaches
              <Badge variant="outline" className="ml-auto text-xs font-normal">
                {referralBoard.total_referred_signups} signup{referralBoard.total_referred_signups === 1 ? '' : 's'} all-time
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {referralBoard.leaderboard.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center" data-testid="ops-referral-leaderboard-empty">
                No referred signups yet. Coaches earn a spot here when someone signs up via their public share link.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-gray-500 border-b">
                    <tr>
                      <th className="text-left py-2 pr-3">#</th>
                      <th className="text-left py-2 pr-3">Coach</th>
                      <th className="text-left py-2 pr-3">Team</th>
                      <th className="text-left py-2 pr-3">Slug</th>
                      <th className="py-2 text-right"><Users className="inline w-3.5 h-3.5 mr-1" strokeWidth={2.5} />Signups</th>
                    </tr>
                  </thead>
                  <tbody data-testid="ops-referral-leaderboard-rows">
                    {referralBoard.leaderboard.map((r, i) => (
                      <tr key={r.public_slug} className="border-b last:border-0 text-gray-800">
                        <td className="py-2 pr-3 font-bold text-emerald-700">{i + 1}</td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            {r.avatar ? (
                              <img
                                src={r.avatar.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${r.avatar}` : r.avatar}
                                alt=""
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center text-[10px] font-bold text-emerald-700">
                                {(r.coach_name?.[0] || 'C').toUpperCase()}
                              </div>
                            )}
                            <span className="font-medium">{r.coach_name}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-gray-600">{r.team_name || '—'}</td>
                        <td className="py-2 pr-3">
                          <a
                            href={`/coach/${r.public_slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-700 hover:underline text-xs font-mono"
                          >
                            {r.public_slug}
                          </a>
                        </td>
                        <td className="py-2 text-right">
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 font-bold text-xs">
                            {r.referred_count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backups Card */}
        <Card data-testid="ops-backups-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="w-4 h-4 text-gray-500" />
              Mongo Backups
            </CardTitle>
            <Button
              size="sm"
              onClick={runBackup}
              disabled={backingUp}
              data-testid="ops-run-backup-btn"
            >
              <PlayCircle className={`w-4 h-4 mr-1 ${backingUp ? 'animate-pulse' : ''}`} />
              {backingUp ? 'Running…' : 'Run backup now'}
            </Button>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-3">
              Directory: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{backups.directory}</code>
              {' · Retention: 7 days'}
            </p>
            {backups.recent && backups.recent.length > 0 ? (
              <div className="space-y-1.5">
                {backups.recent.map((a) => (
                  <div
                    key={a.name}
                    className="flex items-center justify-between text-sm border-b last:border-0 pb-1.5 last:pb-0"
                  >
                    <span className="font-mono text-xs text-gray-700 truncate mr-2">{a.name}</span>
                    <div className="flex items-center gap-3 text-xs text-gray-500 shrink-0">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {fmtAgo(a.modified_at)}
                      </span>
                      <span>{fmtBytes(a.size_bytes)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No archives found. Run a backup or wire cron on the production host.</p>
            )}
          </CardContent>
        </Card>

        {/* Webhook + Rate limits */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card data-testid="ops-webhook-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Webhook className="w-4 h-4 text-gray-500" />
                Stripe Webhook
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row
                label="Endpoint"
                value={<code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{webhook.endpoint}</code>}
              />
              <Row
                label="Signature verification"
                value={
                  webhook.secret_configured ? (
                    <ToneBadge tone="ok">Enabled</ToneBadge>
                  ) : (
                    <ToneBadge tone="warn">Dev fallback</ToneBadge>
                  )
                }
              />
              {!webhook.secret_configured && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Paste your live secret into <code>STRIPE_WEBHOOK_SECRET</code> in
                  <code className="mx-1">/app/backend/.env</code> before going live.
                  Find it under Stripe Dashboard → Developers → Webhooks → your endpoint.
                </p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="ops-ratelimit-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="w-4 h-4 text-gray-500" />
                Auth Rate Limits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              <Row label="Storage" value={<ToneBadge tone="neutral">{limits.storage}</ToneBadge>} />
              {Object.entries(limits.endpoints || {}).map(([ep, lim]) => (
                <Row
                  key={ep}
                  label={<code className="text-xs">{ep}</code>}
                  value={<ToneBadge tone="ok">{lim}</ToneBadge>}
                />
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Email Deliverability Test */}
        <EmailDeliverabilityCard defaultEmail={user?.email} />

        {/* AI Chat Assistant knowledge management */}
        <ChatAssistantCard />

        <p className="text-xs text-gray-400 text-center pt-2">
          Signed in as {user?.email} · Read-only snapshot for super admins
        </p>
      </div>
    </div>
  );
}

function StatusTile({ tone, icon, label, value, sub, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <Card
      data-testid={`ops-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={clickable ? onClick : undefined}
      className={clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="text-gray-500">{icon}</div>
          <StatusIcon tone={tone} />
        </div>
        <p className="text-xs uppercase tracking-wide text-gray-500 mt-2">{label}</p>
        <p className="text-xl font-semibold text-gray-900 mt-0.5 truncate">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{sub}</p>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="text-gray-900">{value}</span>
    </div>
  );
}

function ReadinessCard({ readiness }) {
  if (!readiness) return null;
  const { ready, passed_count, total, checks } = readiness;

  const headerTone = ready
    ? 'from-emerald-50 to-emerald-100 border-emerald-300'
    : 'from-amber-50 to-amber-100 border-amber-300';
  const titleColor = ready ? 'text-emerald-900' : 'text-amber-900';
  const HeaderIcon = ready ? Rocket : AlertTriangle;
  const headerIconColor = ready ? 'text-emerald-600' : 'text-amber-600';

  return (
    <Card data-testid="readiness-card" className={`border bg-gradient-to-br ${headerTone}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <HeaderIcon className={`w-6 h-6 mt-0.5 ${headerIconColor}`} />
            <div>
              <h2 className={`text-lg font-semibold ${titleColor}`} data-testid="readiness-title">
                {ready ? 'Ready for live launch' : `Not ready — ${total - passed_count} blocker${total - passed_count === 1 ? '' : 's'}`}
              </h2>
              <p className="text-sm text-gray-700">
                {passed_count} of {total} pre-launch checks passing.
                {ready
                  ? ' Safe to flip Stripe to live mode and link the custom domain.'
                  : ' Resolve the items below before flipping Stripe to live mode.'}
              </p>
            </div>
          </div>
          <Badge
            variant="outline"
            className={
              ready
                ? 'bg-emerald-600 text-white border-emerald-700 px-3 py-1 text-sm'
                : 'bg-amber-600 text-white border-amber-700 px-3 py-1 text-sm'
            }
            data-testid="readiness-badge"
          >
            {ready ? 'GO' : 'HOLD'}
          </Badge>
        </div>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {checks.map((c) => (
            <div
              key={c.id}
              data-testid={`readiness-check-${c.id}`}
              className={`flex items-start gap-2 rounded-md border px-3 py-2 ${
                c.passed
                  ? 'bg-white/70 border-emerald-200'
                  : 'bg-white border-amber-300'
              }`}
            >
              {c.passed ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              )}
              <div className="text-sm">
                <p className={`font-medium ${c.passed ? 'text-gray-800' : 'text-gray-900'}`}>
                  {c.label}
                </p>
                {c.hint && (
                  <p className="text-xs text-gray-600 mt-0.5">{c.hint}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function EmailDeliverabilityCard({ defaultEmail }) {
  const [recipient, setRecipient] = useState(defaultEmail || '');
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { ok, message, sent_at }

  // Keep input synced if defaultEmail loads after first render
  useEffect(() => {
    if (!recipient && defaultEmail) setRecipient(defaultEmail);
  }, [defaultEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const target = recipient.trim();
    if (!target || !/.+@.+\..+/.test(target)) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await withRetry(() => axios.post(
        `${API}/super-admin/ops/email-test`,
        { to_email: target },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      ));
      setLastResult({ ok: true, message: `Sent to ${res.data.to}`, sent_at: res.data.sent_at });
      toast.success(`Test email sent to ${res.data.to}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Send failed';
      setLastResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card data-testid="ops-email-test-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Mail className="w-4 h-4 text-gray-500" />
          Email Deliverability Test
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          Fires a sample transactional email with all the headers we ship (Reply-To, List-Unsubscribe, multipart text/html).
          Paste the address from{' '}
          <a
            href="https://www.mail-tester.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-700 hover:underline font-medium"
          >
            mail-tester.com
          </a>
          {' '}to get a deliverability score, or use your own inbox to inspect the raw headers.
        </p>

        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="test-xxx@mail-tester.com or you@example.com"
            className="flex-1"
            disabled={sending}
            data-testid="ops-email-test-input"
          />
          <Button
            onClick={send}
            disabled={sending}
            data-testid="ops-email-test-send-btn"
            className="shrink-0"
          >
            <Send className={`w-4 h-4 mr-1 ${sending ? 'animate-pulse' : ''}`} />
            {sending ? 'Sending…' : 'Send test'}
          </Button>
        </div>

        {lastResult && (
          <div
            data-testid="ops-email-test-result"
            className={`text-xs rounded-md border px-3 py-2 ${
              lastResult.ok
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {lastResult.ok ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
            {lastResult.message}
            {lastResult.sent_at && <span className="text-gray-500 ml-2">· {lastResult.sent_at}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



// ─────────────────────────────────────────────────────────────────
// Beta Mode toggle — controls the public "Closed Beta" banner shown
// at the top of the landing page during the soft-launch window.
// ─────────────────────────────────────────────────────────────────
function BetaModeCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [loaded, setLoaded] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await withRetry(() => axios.get(`${API}/super-admin/beta-mode`, { headers, timeout: 15000 }));
      setEnabled(!!res.data.enabled);
      setMessage(res.data.message || '');
      setUpdatedAt(res.data.updated_at);
    } catch (_) {
      toast.error('Could not load beta mode setting');
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (nextEnabled = enabled, nextMessage = message) => {
    setSaving(true);
    try {
      const { data } = await withRetry(() => axios.put(
        `${API}/super-admin/beta-mode`,
        { enabled: nextEnabled, message: nextMessage },
        { headers, timeout: 15000 }
      ));
      setEnabled(data.enabled);
      setMessage(data.message);
      setUpdatedAt(data.updated_at);
      toast.success(`Beta banner ${data.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update beta mode');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="beta-mode-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-base">
            <Megaphone className="w-5 h-5 text-emerald-600" />
            Soft-launch banner
            <ToneBadge tone={enabled ? 'ok' : 'neutral'}>
              {enabled ? 'LIVE' : 'OFF'}
            </ToneBadge>
          </span>
          <Switch
            checked={enabled}
            disabled={!loaded || saving}
            onCheckedChange={(v) => save(v, message)}
            data-testid="beta-mode-toggle"
          />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500">
          When ON, a dismissible green bar appears at the top of the public landing page. Use it during the soft-launch
          window (Stripe in test mode) to let visitors know payments are not yet active.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Closed Beta · No payment required during beta."
            disabled={saving}
            className="flex-1"
            data-testid="beta-mode-message-input"
          />
          <Button
            size="sm"
            onClick={() => save(enabled, message)}
            disabled={saving || !loaded}
            data-testid="beta-mode-save-btn"
          >
            Save message
          </Button>
        </div>
        {updatedAt && (
          <p className="text-[11px] text-gray-400">
            Last changed {fmtAgo(updatedAt)}
          </p>
        )}
      </CardContent>
    </Card>
  );
}



// ─────────────────────────────────────────────────────────────────
// CMS Export / Import — move landing-page content + admin settings
// between preview and production. The two environments are isolated
// MongoDB databases, so this is the supported way to keep them in
// sync (e.g. polish content in preview, then push to production).
// ─────────────────────────────────────────────────────────────────
function CmsTransferCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // parsed JSON awaiting confirmation
  const fileRef = useRef(null);

  const handleExport = async () => {
    setBusy(true);
    try {
      // Embedded image binaries can make the payload large; allow generous timeout.
      const { data } = await withRetry(() => axios.get(`${API}/super-admin/cms/export`, { headers, timeout: 120000 }));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `grassroots-cms-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const collCount = Object.keys(data.collections || {}).length;
      const assetCount = data.asset_count || Object.keys(data.assets || {}).length;
      const sizeMb = (blob.size / (1024 * 1024)).toFixed(1);
      toast.success(`Exported ${collCount} collection(s) + ${assetCount} asset(s) · ${sizeMb} MB`);
      if (data.asset_warnings?.length) {
        toast.warning(`${data.asset_warnings.length} asset warning(s) — see browser console`);
        console.warn('[CMS export] asset warnings:', data.asset_warnings);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed.version || !parsed.collections) {
        throw new Error('File does not look like a CMS export — missing version/collections.');
      }
      setPending({ filename: file.name, ...parsed });
    } catch (err) {
      toast.error(`Could not read file: ${err.message}`);
    } finally {
      if (fileRef.current) fileRef.current.value = ''; // allow re-selecting the same file
    }
  };

  const confirmImport = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { version, collections, assets } = pending;
      const { data } = await withRetry(() => axios.post(
        `${API}/super-admin/cms/import`,
        { version, collections, assets },
        { headers, timeout: 180000 }
      ));
      const total = Object.entries(data.summary || {})
        .filter(([k]) => k !== 'assets')
        .reduce((s, [, c]) => s + (c.upserted || 0), 0);
      const assetSummary = data.summary?.assets;
      const assetMsg = assetSummary
        ? ` + ${assetSummary.restored} asset(s) restored${assetSummary.errors?.length ? ` (${assetSummary.errors.length} error(s))` : ''}`
        : '';
      toast.success(`Imported ${total} document(s)${assetMsg} — refresh the landing page to see changes`);
      if (assetSummary?.errors?.length) {
        console.warn('[CMS import] asset errors:', assetSummary.errors);
      }
      setPending(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="cms-transfer-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Download className="w-5 h-5 text-emerald-600" />
          CMS Content Transfer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-500">
          Move landing-page CMS content and admin settings between this environment and the other.
          Includes <span className="font-mono">landing_content</span> + <span className="font-mono">app_settings</span>.
          Typical workflow: polish content in preview → <strong>Export</strong> here → <strong>Import</strong> in production CMS.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button onClick={handleExport} disabled={busy} size="sm" variant="outline" data-testid="cms-export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Export JSON
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={busy} size="sm" data-testid="cms-import-pick-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            Import from file…
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            onChange={handleFilePicked}
            className="hidden"
            data-testid="cms-import-file-input"
          />
        </div>

        {pending && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 space-y-2" data-testid="cms-import-confirm">
            <p className="text-xs text-amber-900 font-semibold">Confirm import — this will overwrite matching documents in this environment.</p>
            <ul className="text-[11px] text-amber-900 font-mono space-y-0.5">
              <li>File: {pending.filename}</li>
              <li>Exported at: {pending.exported_at || 'unknown'}</li>
              <li>By: {pending.exported_by || 'unknown'}</li>
              {Object.entries(pending.collections || {}).map(([k, arr]) => (
                <li key={k}>{k}: {Array.isArray(arr) ? arr.length : '?'} doc(s)</li>
              ))}
              {pending.assets && (
                <li>assets (images/videos): {Object.keys(pending.assets).length} file(s)</li>
              )}
            </ul>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={confirmImport} disabled={busy} data-testid="cms-import-confirm-btn">
                Confirm import
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPending(null)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * ChatAssistantCard — surfaces the live system prompt used by the AI chat
 * widget and lets the admin (a) preview exactly what the bot currently
 * "knows", (b) refresh with a one-tap button, and (c) edit optional override
 * notes that get appended verbatim to every conversation.
 */
function ChatAssistantCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await withRetry(() => axios.get(`${API}/super-admin/chat-assistant/status`, { headers, timeout: 15000 }));
      setStatus(data);
      setOverrideDraft(data?.admin_override_notes || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load chat assistant status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doRefresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await withRetry(() => axios.post(`${API}/super-admin/chat-assistant/refresh`, {}, { headers, timeout: 20000 }));
      setStatus(prev => ({ ...(prev || {}), ...data, admin_override_notes: prev?.admin_override_notes || '' }));
      toast.success(data?.message || 'AI Assistant knowledge refreshed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const saveOverride = async () => {
    setSavingOverride(true);
    try {
      const { data } = await withRetry(() => axios.put(
        `${API}/super-admin/chat-assistant/override`,
        { admin_override_notes: overrideDraft },
        { headers, timeout: 20000 }
      ));
      setStatus(prev => ({ ...(prev || {}), ...data }));
      toast.success(data?.message || 'Override notes saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingOverride(false);
    }
  };

  const overrideChanged = (status?.admin_override_notes || '') !== overrideDraft;
  const lastRefreshedLabel = status?.last_refreshed_at
    ? new Date(status.last_refreshed_at).toLocaleString()
    : 'Never (uses live CMS data on each conversation)';

  return (
    <Card data-testid="ops-chat-assistant-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bot className="w-5 h-5 text-indigo-600" strokeWidth={1.5} />
          AI Chat Assistant Knowledge
        </CardTitle>
        <p className="text-sm text-gray-500">
          The chat widget builds its answers from live CMS data every conversation, so it stays in sync with pricing, features and offers. Use <strong>Refresh</strong> after big CMS edits for a hard confirmation, and add optional override notes to nudge tone or add seasonal rules.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-400">Loading assistant status\u2026</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Last refreshed</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{lastRefreshedLabel}</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Prompt length</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{status?.char_count || 0} chars</p>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">Override notes</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{status?.admin_override_notes ? 'Active' : 'None'}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={doRefresh}
                disabled={refreshing}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="ops-chat-refresh-btn"
              >
                <Sparkles className={`w-4 h-4 mr-2 ${refreshing ? 'animate-pulse' : ''}`} strokeWidth={2} />
                {refreshing ? 'Refreshing\u2026' : 'Refresh AI Knowledge'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPreview(v => !v)}
                data-testid="ops-chat-preview-toggle-btn"
              >
                {showPreview ? 'Hide' : 'Preview'} current prompt
              </Button>
            </div>

            {showPreview && (
              <pre
                className="text-[11px] bg-gray-900 text-gray-100 p-3 rounded-lg max-h-72 overflow-auto whitespace-pre-wrap font-mono"
                data-testid="ops-chat-prompt-preview"
              >
                {status?.prompt || '(empty)'}
              </pre>
            )}

            <div className="pt-2 border-t border-gray-200">
              <label className="text-sm font-semibold text-gray-800 block mb-1">
                Admin override notes <span className="text-xs font-normal text-gray-500">(optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Free-text appended to every conversation. Use for tone rules, seasonal offers or must-say disclaimers. Leave empty to skip.
              </p>
              <textarea
                value={overrideDraft}
                onChange={(e) => setOverrideDraft(e.target.value)}
                placeholder={"e.g. Mention our June kit giveaway when a visitor asks about signup bonuses. Never quote refund percentages \u2014 escalate to a human agent."}
                rows={4}
                className="w-full text-sm rounded-lg border border-gray-300 p-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 font-mono"
                data-testid="ops-chat-override-input"
              />
              <div className="flex justify-end mt-2">
                <Button
                  onClick={saveOverride}
                  disabled={!overrideChanged || savingOverride}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
                  data-testid="ops-chat-override-save-btn"
                >
                  {savingOverride ? 'Saving\u2026' : (overrideChanged ? 'Save override notes' : 'No changes')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
